import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type {
  Category,
  OrderTemplate,
  Beneficiary,
} from '@/services/orderApi'

export interface AppointmentSchedule {
  appointmentSpecId: string
  scheduledStart: string
  label: string
}

// Step labels for progress indicator
export const ORDER_CREATION_STEPS = [
  { number: 1, label: 'Client', labelAr: 'العميل' },
  { number: 2, label: 'Category', labelAr: 'الفئة' },
  { number: 3, label: 'Template', labelAr: 'القالب' },
  { number: 4, label: 'Schedule', labelAr: 'الجدول' },
  { number: 5, label: 'Beneficiary', labelAr: 'المستفيد' },
  { number: 6, label: 'Questions', labelAr: 'الأسئلة' },
  { number: 7, label: 'Payment', labelAr: 'الدفع' },
] as const

interface OrderCreationState {
  // Step 1: Client Selection (entity creates for another client)
  clientProfileId: string | null
  clientName: string | null
  clientPhone: string | null
  clientEmail: string | null
  setClient: (id: string, name: string, phone?: string, email?: string) => void
  setClientProfileId: (id: string | null) => void

  /** Business client ordering for themselves; REST APIs use business_client_profile_id. */
  businessClientProfileId: string | null
  /** Business account used only for B2B catalog GraphQL scoping. */
  businessAccountId: string | null
  setBusinessClientSelfOrder: (
    profileId: string,
    displayName: string,
    phone?: string | null,
    email?: string | null,
    businessAccountId?: string | null
  ) => void

  // Step 2: Category Selection
  selectedCategory: Category | null
  setSelectedCategory: (category: Category | null) => void

  // Step 3: Template Selection
  selectedTemplate: OrderTemplate | null
  setSelectedTemplate: (template: OrderTemplate | null) => void

  // Step 4: Appointment Configuration
  appointments: AppointmentSchedule[]
  setAppointments: (appointments: AppointmentSchedule[]) => void
  addAppointment: (appointment: AppointmentSchedule) => void
  updateAppointment: (index: number, appointment: AppointmentSchedule) => void
  removeAppointment: (index: number) => void

  // Step 5: Beneficiary Selection
  selectedBeneficiary: Beneficiary | null
  setSelectedBeneficiary: (beneficiary: Beneficiary | null) => void
  isSelfBeneficiary: boolean
  setIsSelfBeneficiary: (isSelf: boolean) => void

  // Step 6: Questions
  evaluationSnapshotId: string | null
  setEvaluationSnapshotId: (id: string | null) => void
  evaluationSnapshot: any | null
  setEvaluationSnapshot: (snapshot: any | null) => void
  evaluationResponseId: string | null
  setEvaluationResponseId: (id: string | null) => void

  // Step 7: Order & Payment
  orderId: string | null
  orderNumber: string | null
  setOrderId: (id: string | null) => void
  setOrder: (orderId: string, orderNumber: string) => void
  invoiceId: string | null
  setInvoiceId: (id: string | null) => void
  transactionId: string | null
  setTransactionId: (id: string | null) => void

  // Navigation
  currentStep: number
  setCurrentStep: (step: number) => void
  goToNextStep: () => void
  goToPreviousStep: () => void

  // Computed helpers
  getProgress: () => number
  canProceedFromStep: (step: number) => boolean

  // Reset function
  reset: () => void

  /** First route of the flow: client picker vs category (business self-order). */
  getCreateOrderEntryPath: () => string
}

const initialState = {
  // Client / business self-order
  clientProfileId: null,
  businessClientProfileId: null,
  businessAccountId: null,
  clientName: null,
  clientPhone: null,
  clientEmail: null,
  // Category & Template
  selectedCategory: null,
  selectedTemplate: null,
  // Appointments
  appointments: [],
  // Beneficiary
  selectedBeneficiary: null,
  isSelfBeneficiary: false,
  // Evaluation
  evaluationSnapshotId: null,
  evaluationSnapshot: null,
  evaluationResponseId: null,
  // Order
  orderId: null,
  orderNumber: null,
  invoiceId: null,
  transactionId: null,
  // Navigation
  currentStep: 1,
}

export const useOrderCreationStore = create<OrderCreationState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Step 1: Client (entity flow)
      setClient: (id, name, phone, email) =>
        set({
          clientProfileId: id,
          businessClientProfileId: null,
          businessAccountId: null,
          clientName: name,
          clientPhone: phone ?? null,
          clientEmail: email ?? null,
        }),

      setClientProfileId: (id) =>
        set(
          id
            ? {
                clientProfileId: id,
                businessClientProfileId: null,
                businessAccountId: null,
              }
            : { clientProfileId: id }
        ),

      setBusinessClientSelfOrder: (profileId, displayName, phone, email, businessAccountId) =>
        set({
          businessClientProfileId: profileId,
          businessAccountId: businessAccountId ?? null,
          clientProfileId: null,
          clientName: displayName,
          clientPhone: phone ?? null,
          clientEmail: email ?? null,
          currentStep: 2,
        }),

      // Step 2: Category
      setSelectedCategory: (category) =>
        set({
          selectedCategory: category,
          // Reset downstream selections when category changes
          selectedTemplate: null,
        }),

      // Step 3: Template
      setSelectedTemplate: (template) => set({ selectedTemplate: template }),

      // Step 4: Appointments
      setAppointments: (appointments) => set({ appointments }),
      addAppointment: (appointment) =>
        set((state) => ({
          appointments: [...state.appointments, appointment],
        })),
      updateAppointment: (index, appointment) =>
        set((state) => {
          const updated = [...state.appointments]
          updated[index] = appointment
          return { appointments: updated }
        }),
      removeAppointment: (index) =>
        set((state) => ({
          appointments: state.appointments.filter((_, i) => i !== index),
        })),

      // Step 5: Beneficiary
      setSelectedBeneficiary: (beneficiary) =>
        set({ selectedBeneficiary: beneficiary }),
      setIsSelfBeneficiary: (isSelf) => set({ isSelfBeneficiary: isSelf }),

      // Step 6: Evaluation/Questions
      setEvaluationSnapshotId: (id) => set({ evaluationSnapshotId: id }),
      setEvaluationSnapshot: (snapshot) =>
        set({ evaluationSnapshot: snapshot }),
      setEvaluationResponseId: (id) => set({ evaluationResponseId: id }),

      // Step 7: Order & Payment
      setOrderId: (id) => set({ orderId: id }),
      setOrder: (orderId, orderNumber) => set({ orderId, orderNumber }),
      setInvoiceId: (id) => set({ invoiceId: id }),
      setTransactionId: (id) => set({ transactionId: id }),

      // Navigation
      setCurrentStep: (step) => set({ currentStep: step }),
      goToNextStep: () =>
        set((state) => ({ currentStep: Math.min(state.currentStep + 1, 7) })),
      goToPreviousStep: () =>
        set((state) => ({ currentStep: Math.max(state.currentStep - 1, 1) })),

      // Computed helpers
      getProgress: () => {
        const state = get()
        return Math.round((state.currentStep / 7) * 100)
      },

      canProceedFromStep: (step: number) => {
        const state = get()
        switch (step) {
          case 1:
            return !!state.clientProfileId || !!state.businessClientProfileId
          case 2:
            return !!state.selectedCategory
          case 3:
            return !!state.selectedTemplate
          case 4:
            return (
              state.appointments.length > 0 &&
              state.appointments.every((apt) => apt.scheduledStart)
            )
          case 5:
            return !!state.selectedBeneficiary
          case 6:
            // Can proceed if no snapshot OR response submitted
            return !state.evaluationSnapshotId || !!state.evaluationResponseId
          case 7:
            return !!state.transactionId
          default:
            return false
        }
      },

      reset: () => set(initialState),

      getCreateOrderEntryPath: () => {
        const s = get()
        return s.businessClientProfileId ? '/create-order/category' : '/create-order'
      },
    }),
    {
      name: 'order-creation-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Persist essential fields (exclude large objects like evaluationSnapshot)
        clientProfileId: state.clientProfileId,
        businessClientProfileId: state.businessClientProfileId,
        businessAccountId: state.businessAccountId,
        clientName: state.clientName,
        clientPhone: state.clientPhone,
        clientEmail: state.clientEmail,
        selectedCategory: state.selectedCategory,
        selectedTemplate: state.selectedTemplate,
        appointments: state.appointments,
        selectedBeneficiary: state.selectedBeneficiary,
        isSelfBeneficiary: state.isSelfBeneficiary,
        evaluationSnapshotId: state.evaluationSnapshotId,
        evaluationResponseId: state.evaluationResponseId,
        orderId: state.orderId,
        orderNumber: state.orderNumber,
        invoiceId: state.invoiceId,
        transactionId: state.transactionId,
        currentStep: state.currentStep,
      }),
    }
  )
)

/** Persist name in `persist(..., { name })` - used for explicit session cleanup */
export const ORDER_CREATION_STORAGE_KEY = 'order-creation-storage'

export function clearOrderCreationPersistedState(): void {
  try {
    useOrderCreationStore.getState().reset()
  } catch {
    // ignore
  }
  try {
    useOrderCreationStore.persist.clearStorage()
  } catch {
    // ignore
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('auth:logout', () => {
    clearOrderCreationPersistedState()
  })
}
